import React, { useState, useEffect } from "react";
import { Create } from "@refinedev/antd";
import { useCustomMutation, useOne } from "@refinedev/core";
import { message, Form } from "antd";
import { PurchaseOrderForm } from "./components/PurchaseOrderForm";
import { PurchaseRequestSelector } from "./components/PurchaseRequestSelector";
import {
  CREATE_PURCHASE_ORDER_MUTATION,
  SUBMIT_PURCHASE_ORDER_MUTATION,
  CREATE_PURCHASE_ORDER_FROM_REQUEST_MUTATION,
} from "./queries";
import { useLocation } from "react-router-dom";
import { PURCHASE_REQUEST_ONE_QUERY } from "../purchase-request/queries";
import { PurchaseRequest } from "../purchase-request/interface";

export const CreatePurchaseOrder: React.FC = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fromPRId = queryParams.get("fromPR");

  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = useState<PurchaseRequest | null>(null);
  const [isLinkingMode, setIsLinkingMode] = useState<boolean>(!!fromPRId);

  const { data: prData, isLoading: prLoading } = useOne<PurchaseRequest>({
    resource: "purchaseRequests",
    id: fromPRId || "",
    queryOptions: {
      enabled: !!fromPRId,
    },
    meta: {
      gqlQuery: PURCHASE_REQUEST_ONE_QUERY,
    },
  });

  useEffect(() => {
    if (prData?.data) {
      setSelectedPurchaseRequest(prData.data);
    }
  }, [prData]);

  // Create draft
  const { mutate: createMutate, isLoading: creating } = useCustomMutation();
  // Submit PO (create + submit path via orderData)
  const { mutate: submitMutate, isLoading: submitting } = useCustomMutation();
  // Create PO from PR
  const { mutate: createFromPrMutate, isLoading: creatingFromPr } = useCustomMutation();

  const onSaveDraft = async (values: any) => {
    if (selectedPurchaseRequest) {
      createFromPrMutate(
        {
          url: "",
          method: "post",
          values: {
            input: {
              ...values,
              purchaseRequestId: selectedPurchaseRequest.id,
            },
          },
          meta: { gqlMutation: CREATE_PURCHASE_ORDER_FROM_REQUEST_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Purchase Order created from Request and saved as draft");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to create PO from Request and save as draft");
          },
        },
      );
    } else {
      createMutate(
        {
          url: "",
          method: "post",
          values: {
            input: values,
          },
          meta: { gqlMutation: CREATE_PURCHASE_ORDER_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Purchase Order saved as draft");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to save draft");
          },
        },
      );
    }
  };

  const onSubmit = async (payload: { orderId?: string; orderData?: any }) => {
    // In create page we expect orderData path
    const orderData = payload.orderData ?? {};

    if (selectedPurchaseRequest) {
      createFromPrMutate(
        {
          url: "",
          method: "post",
          values: {
            input: {
              ...orderData,
              purchaseRequestId: selectedPurchaseRequest.id,
            },
          },
          meta: { gqlMutation: CREATE_PURCHASE_ORDER_FROM_REQUEST_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Purchase Order created from Request and submitted");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to create PO from Request and submit");
          },
        },
      );
    } else {
      submitMutate(
        {
          url: "",
          method: "post",
          values: {
            input: {
              orderData: orderData,
            },
          },
          meta: { gqlMutation: SUBMIT_PURCHASE_ORDER_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Purchase Order submitted");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to submit order");
          },
        },
      );
    }
  };

  // if (prLoading) {
  //   return <Spin tip="Loading Purchase Request..." />;
  // }

  return (
    <Create
      title="Create Purchase Order"
      saveButtonProps={{ loading: creating || submitting || creatingFromPr, style: { display: "none" } }}
      footerButtons={() => null}
    >
      {!isLinkingMode && (
        <PurchaseRequestSelector
          onSelect={(pr) => {
            setSelectedPurchaseRequest(pr);
            if (pr) {
              setIsLinkingMode(true); // Switch to form view once PR is selected
            }
          }}
          initialPurchaseRequestId={fromPRId || undefined}
        />
      )}

      {isLinkingMode && (
        <PurchaseOrderForm
          form={form}
          isEdit={false}
          onSaveDraft={onSaveDraft}
          onSubmit={onSubmit}
          initialPurchaseRequest={selectedPurchaseRequest || undefined}
        />
      )}
    </Create>
  );
};